import {
  clearNuxtData,
  refreshCookie,
  useAsyncData,
  useCookie,
  useFetch,
  useLazyFetch,
  useNuxtApp,
  useRequestHeaders,
  useRuntimeConfig,
  useRoute,
  useState
} from '#app';
import { computed, ref, unref } from 'vue';
import { useI18n } from 'vue-i18n';

const ALLOWED_LOCALES = [
  'en',
  'de',
  'tr',
  'fr',
  'es',
  'pt',
  'it',
  'ar',
  'hi',
  'ur',
  'ru',
  'ja',
  'zh'
];

const PUBLIC_AUTH_COOKIE = 'idmanify_user';

const API_CACHE_EXCLUDE_PATHS = [
  '/api/user',
  '/api/auth',
  '/api/payment',
  '/api/check-c',
  'payment-result',
  '/payment-auth',
  '/payment/result/',
  '/api/create-payment-intent',
  '/api/payment/create-payment-intent'
];

const normalizeLocale = (value = '') => {
  const locale = String(value || '')
    .trim()
    .toLowerCase()
    .slice(0, 2);
  return ALLOWED_LOCALES.includes(locale) ? locale : '';
};

const getPrefixedLocaleFromPath = (path = '') => {
  const firstSegment = String(path || '').split('/')[1] || '';
  return normalizeLocale(firstSegment);
};

const getLocaleFromPath = (path = '') =>
  getPrefixedLocaleFromPath(path) || 'en';

const getLocaleByUrl = () => {
  const route = useRoute();
  return getLocaleFromPath(route.path);
};

const resolveObject = (value) => {
  const resolved = unref(value);
  return resolved && typeof resolved === 'object' ? resolved : {};
};

const stableSortedQuery = (query) => {
  const out = {};
  Object.keys(query || {})
    .sort()
    .forEach((key) => {
      const value = query[key];
      if (value !== undefined && value !== null) out[key] = value;
    });
  return out;
};

const getPublicConfigSafely = () => {
  try {
    return useRuntimeConfig().public || {};
  } catch {
    return globalThis?.__NUXT__?.config?.public || {};
  }
};

const getNuxtAppSafely = (nuxtApp) => {
  if (nuxtApp) return nuxtApp;

  try {
    return useNuxtApp();
  } catch {
    return null;
  }
};

const getAuthSafely = (nuxtApp) => {
  const app = getNuxtAppSafely(nuxtApp);
  return app?.$auth || globalThis?.__IDMANIFY_AUTH__ || null;
};

const getBrowserCookie = (name) => {
  if (!import.meta.client || typeof document === 'undefined') return null;

  const cookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));

  return cookie ? cookie.slice(name.length + 1) : null;
};

const readCookieValueSafely = (name) => {
  const browserCookie = getBrowserCookie(name);
  if (browserCookie !== null) return browserCookie;

  try {
    return useCookie(name).value;
  } catch {
    return null;
  }
};

const refreshCookieSafely = (name) => {
  if (!import.meta.client) return;

  try {
    refreshCookie(name);
  } catch {
    // Best effort only; outside setup/plugin contexts document.cookie is enough.
  }
};

const readXsrfHeader = () => {
  const token = readCookieValueSafely('XSRF-TOKEN');
  return token ? { 'X-XSRF-TOKEN': decodeURIComponent(token) } : {};
};

const cookieHeaderHas = (headers = {}, name) => {
  const cookieHeader = headers.cookie || headers.Cookie || '';
  return String(cookieHeader)
    .split(';')
    .some((cookie) => cookie.trim().startsWith(`${name}=`));
};

export const hasAuthenticatedUserHint = (serverRequestHeaders = {}) => {
  try {
    refreshCookieSafely(PUBLIC_AUTH_COOKIE);
    if (readCookieValueSafely(PUBLIC_AUTH_COOKIE)) return true;
  } catch {
    // Cookie hint is best effort only; the server session remains authoritative.
  }

  return cookieHeaderHas(serverRequestHeaders, PUBLIC_AUTH_COOKIE);
};

const normalizeApiPath = (request, apiBase = '') => {
  const raw = String(request || '');
  const base = String(apiBase || '').replace(/\/$/, '');

  if (base && raw.startsWith(base)) return raw.slice(base.length) || '/';

  try {
    return new URL(raw).pathname;
  } catch {
    return raw.split('?')[0];
  }
};

export const isAuthRequiredUserRequest = (request, apiBase = '') => {
  const path = normalizeApiPath(request, apiBase);
  return path === '/api/auth/user' || path.startsWith('/api/user');
};

const getDefaultFetchValue = (opts = {}) =>
  typeof opts.default === 'function' ? opts.default() : (opts.default ?? null);

const getAuthCacheSegment = (loggedIn = false) => {
  if (loggedIn) return 'auth:session';

  try {
    refreshCookieSafely(PUBLIC_AUTH_COOKIE);
    if (readCookieValueSafely(PUBLIC_AUTH_COOKIE)) return 'auth:session';
  } catch {
    // Public mirror cookie is a cache hint only, never an auth proof.
  }

  return 'auth:guest';
};

const buildCacheKey = (
  request,
  query,
  locale,
  apiBase,
  authSegment = '',
  currencySegment = ''
) => {
  const base = String(apiBase || '').replace(/\/$/, '');
  const req = String(request || '');
  const path = base && req.startsWith(base) ? req.slice(base.length) : req;
  const queryString = new URLSearchParams(stableSortedQuery(query)).toString();
  return `${path}?${queryString}__${locale}${authSegment ? `__${authSegment}` : ''}${currencySegment ? `__${currencySegment}` : ''}`;
};

const shouldCacheRequest = (request, method, loggedIn = false) => {
  if (String(method || 'GET').toUpperCase() !== 'GET') return false;

  const url = String(request || '');
  if (API_CACHE_EXCLUDE_PATHS.some((path) => url.includes(path))) return false;

  // Package prices can be personalized by auth/currency; authenticated payloads
  // must not reuse guest payload cache.
  if (loggedIn && url.includes('/api/packages')) return false;

  return true;
};

const buildMergedQuery = (opts = {}, locale) => ({
  ...resolveObject(opts.query),
  ...resolveObject(opts.params),
  lng: unref(locale)
});

const createCsrfHeaders = (headers = {}) => {
  const csrfHeaders = { ...headers };

  Object.keys(csrfHeaders).forEach((key) => {
    if (key.toLowerCase() === 'content-type') {
      delete csrfHeaders[key];
    }
  });

  return csrfHeaders;
};

const fetchCsrfToken = async (apiBase, headers = {}) => {
  await $fetch('/api/auth/csrf-cookie', {
    baseURL: apiBase,
    method: 'GET',
    credentials: 'include',
    headers: createCsrfHeaders(headers)
  });

  refreshCookieSafely('XSRF-TOKEN');
};

const isCsrfMismatch = (fetchError) => {
  const response = fetchError?.response;
  const message = String(response?._data?.message || fetchError?.message || '');

  return response?.status === 419 || message.toLowerCase().includes('csrf');
};

const buildErrorMessage = (response) => {
  if (response?._data?.errors) {
    return Object.values(response._data.errors)
      .map((items) => items?.[0])
      .filter(Boolean)
      .join('\n');
  }

  return (
    response?._data?.error ||
    response?._data?.message ||
    'Unknown error occurred'
  );
};

const buildFriendlyErrorMessage = (response) => {
  if (response?.status === 429) {
    return 'Zu viele Anfragen. Bitte kurz warten und erneut versuchen.';
  }

  return buildErrorMessage(response);
};

const notifyResponseError = (nuxtApp, response) => {
  const content = buildFriendlyErrorMessage(response);
  if (!content) return;

  nuxtApp?.$notifier?.showMessage({
    content: `Error: ${content}`,
    color: 'red'
  });
};

const getDashboardRefreshScopes = (request, apiBase = '') => {
  const path = normalizeApiPath(request, apiBase);

  if (path.includes('/api/user/ptforms')) {
    return ['status', 'training', 'nutrition'];
  }

  if (path.includes('/api/user/questions')) {
    return ['status', 'questions'];
  }

  if (path.includes('/api/package-reviews')) {
    return ['status'];
  }

  if (path.includes('/api/auth/user') || path.includes('/api/user/')) {
    return ['status'];
  }

  return ['status'];
};

const isDashboardMutationRequest = (request, apiBase = '') => {
  const path = normalizeApiPath(request, apiBase);

  return (
    isAuthRequiredUserRequest(request, apiBase) ||
    path.includes('/api/package-reviews')
  );
};

const dispatchDashboardRefresh = (request, apiBase = '') => {
  if (!import.meta.client) return;

  window.dispatchEvent(
    new CustomEvent('idmanify:userpanel-refresh', {
      detail: { scopes: getDashboardRefreshScopes(request, apiBase) }
    })
  );
};

const createFetchContext = () => {
  const config = useRuntimeConfig();
  const apiBase = config.public.API_URL;
  const nuxtApp = useNuxtApp();
  const auth = getAuthSafely(nuxtApp);
  const serverRequestHeaders = import.meta.server
    ? useRequestHeaders(['cookie'])
    : {};
  const route = useRoute();
  const { locale } = useI18n();
  const currency = useState('currency:code', () => null);

  const localeRef = computed(() => {
    const routeLocale = getPrefixedLocaleFromPath(route.path);
    const i18nLocale = normalizeLocale(locale?.value);

    // During nuxt-i18n route switches the localized slug/path can update before
    // `locale.value`. Prefer an explicit locale prefix from the active route so
    // we never request "new-language slug + old-language lng".
    return routeLocale || i18nLocale || 'en';
  });

  const loggedIn = computed(() => !!auth?.loggedIn?.value);

  const baseHeaders = computed(() => {
    const headers = {
      accept: 'application/json'
    };

    if (import.meta.server) {
      Object.assign(headers, serverRequestHeaders);
      headers.referer = config.public.FRONTEND_URL;
    }

    return headers;
  });

  return {
    apiBase,
    auth,
    baseHeaders,
    config,
    currency,
    localeRef,
    loggedIn,
    nuxtApp,
    serverRequestHeaders
  };
};

const normalizeWatchSources = (watchOption) => {
  if (watchOption === false || watchOption == null) return [];
  return Array.isArray(watchOption) ? watchOption : [watchOption];
};

const createUseFetchOptions = (request, opts, lazy = false) => {
  const {
    apiBase,
    baseHeaders,
    currency,
    loggedIn,
    localeRef,
    nuxtApp,
    serverRequestHeaders,
    config
  } = createFetchContext();

  const requestRef = computed(() =>
    typeof request === 'function' ? request() : unref(request)
  );

  const method = computed(() => String(opts.method || 'GET').toUpperCase());
  const mergedQuery = computed(() =>
    buildMergedQuery(opts, localeRef.value, loggedIn.value)
  );
  const cacheable = computed(() =>
    shouldCacheRequest(requestRef.value, method.value, loggedIn.value)
  );
  const key = computed(() => {
    if (opts.key) return unref(opts.key);
    return buildCacheKey(
      requestRef.value,
      mergedQuery.value,
      localeRef.value,
      apiBase,
      getAuthCacheSegment(loggedIn.value),
      currency.value ? `currency:${currency.value}` : 'currency:unknown'
    );
  });

  return {
    ...opts,
    baseURL: apiBase,
    credentials: 'include',
    headers: computed(() => ({
      ...baseHeaders.value,
      ...(opts.headers || {})
    })),
    query: mergedQuery,
    params: undefined,
    key,
    getCachedData: (cacheKey) => {
      if (!cacheable.value) return undefined;
      return (
        nuxtApp.payload?.data?.[cacheKey] || nuxtApp.static?.data?.[cacheKey]
      );
    },
    dedupe: opts.dedupe || 'cancel',
    lazy: lazy || opts.lazy,
    // Price-bearing API data must refetch when auth/currency changes. A key-only
    // change is not enough in all Nuxt/useFetch paths, so keep explicit sources.
    watch: [
      ...normalizeWatchSources(opts.watch),
      mergedQuery,
      loggedIn,
      currency
    ],
    async onRequest({ options }) {
      const headers = { ...(options.headers || {}) };

      if (import.meta.server) {
        Object.assign(headers, serverRequestHeaders);
        headers.referer = config.public.FRONTEND_URL;
      }

      const requestMethod = String(
        options.method || method.value || 'GET'
      ).toUpperCase();
      if (requestMethod !== 'GET') await fetchCsrfToken(apiBase, headers);

      Object.assign(headers, readXsrfHeader());
      options.headers = headers;
    },
    onResponse(context) {
      opts.onResponse?.(context);
      return context.response._data;
    },
    onResponseError(context) {
      opts.onResponseError?.(context);
      if (!opts.silentError) notifyResponseError(nuxtApp, context.response);
    }
  };
};

export const useLocalizedApiFetch = (request, opts = {}) => {
  return useFetch(request, createUseFetchOptions(request, opts));
};

// Backwards-compatible names. They intentionally share the same implementation
// so auth, cache, locale and CSRF behavior cannot drift again.
export const useLocalizedApiFetchOLD = useLocalizedApiFetch;
export const useLocalizedApiFetchReact = useLocalizedApiFetch;

export const useSsrLocalizedApiFetch = (keyBase, request, opts = {}) => {
  const { apiBase, baseHeaders, currency, localeRef, loggedIn, nuxtApp } =
    createFetchContext();

  const keyBaseRef = computed(() =>
    typeof keyBase === 'function' ? keyBase() : unref(keyBase)
  );
  const requestRef = computed(() =>
    typeof request === 'function' ? request() : unref(request)
  );
  const queryRef = computed(() =>
    buildMergedQuery(opts, localeRef.value, loggedIn.value)
  );
  const key = computed(() =>
    opts.key
      ? unref(opts.key)
      : `${keyBaseRef.value}__${buildCacheKey(
          requestRef.value,
          queryRef.value,
          localeRef.value,
          apiBase,
          getAuthCacheSegment(loggedIn.value),
          currency.value ? `currency:${currency.value}` : 'currency:unknown'
        )}`
  );

  return useAsyncData(
    key,
    async () => {
      return await $fetch(requestRef.value, {
        ...opts,
        baseURL: apiBase,
        credentials: 'include',
        headers: {
          ...baseHeaders.value,
          ...(opts.headers || {}),
          ...readXsrfHeader()
        },
        query: queryRef.value,
        params: undefined
      });
    },
    {
      default: () =>
        typeof opts.default === 'function'
          ? opts.default()
          : (opts.default ?? null),
      getCachedData: (cacheKey) =>
        shouldCacheRequest(
          requestRef.value,
          opts.method || 'GET',
          loggedIn.value
        )
          ? nuxtApp.payload?.data?.[cacheKey] ||
            nuxtApp.static?.data?.[cacheKey]
          : undefined,
      // Optional preview transforms trim API-only fields before payload hydration.
      // Existing callers without a transform keep their original response shape.
      transform: opts.transform,
      watch: [queryRef, loggedIn, currency]
    }
  );
};

export const useSsrLocalizedApiFetchNotReact = useSsrLocalizedApiFetch;

export const useClientApiFetch = async (url, opts = {}) => {
  const publicConfig = getPublicConfigSafely();
  const apiBase = publicConfig.API_URL;
  const method = String(opts.method || 'GET').toUpperCase();
  const data = ref(null);
  const error = ref(null);
  const pending = ref(true);

  const query = buildMergedQuery(
    opts,
    opts.query?.lng ||
      opts.params?.lng ||
      getLocaleFromPath(globalThis?.location?.pathname || ''),
    getAuthCacheSegment() === 'auth:session'
  );
  const headers = {
    accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...(opts.headers || {})
  };

  try {
    if (
      isAuthRequiredUserRequest(url, apiBase) &&
      !hasAuthenticatedUserHint()
    ) {
      data.value = getDefaultFetchValue(opts);
      return { data, error, pending };
    }

    if (method !== 'GET') await fetchCsrfToken(apiBase, headers);
    Object.assign(headers, readXsrfHeader());

    try {
      data.value = await $fetch(url, {
        ...opts,
        baseURL: apiBase,
        credentials: 'include',
        headers,
        query,
        params: undefined
      });
    } catch (fetchError) {
      if (method !== 'GET' && isCsrfMismatch(fetchError)) {
        await fetchCsrfToken(apiBase, headers);
        Object.assign(headers, readXsrfHeader());

        data.value = await $fetch(url, {
          ...opts,
          baseURL: apiBase,
          credentials: 'include',
          headers,
          query,
          params: undefined
        });
      } else {
        throw fetchError;
      }
    }

    if (method !== 'GET' && isDashboardMutationRequest(url, apiBase)) {
      dispatchDashboardRefresh(url, apiBase);
    }
  } catch (fetchError) {
    error.value = fetchError;
    opts.onResponseError?.({
      error: fetchError,
      response: fetchError?.response
    });

    if (!opts.silentError) {
      notifyResponseError(getNuxtAppSafely(), fetchError?.response);
    }
  } finally {
    pending.value = false;
  }

  return { data, error, pending };
};

export const useLocalizedApiLazyFetch = (request, opts = {}) => {
  return useLazyFetch(request, createUseFetchOptions(request, opts, true));
};

export const clearFullCache = (nuxtApp) => {
  const app = getNuxtAppSafely(nuxtApp);
  if (!app) return;

  if (app.payload?.data) app.payload.data = {};
  if (app.static?.data) app.static.data = {};
};

export const clearApiCache = (nuxtApp) => {
  const app = getNuxtAppSafely(nuxtApp);
  const isApiKey = (key) => key.startsWith('/api') || key.includes('/api/');

  if (app?.payload?.data) {
    for (const key in app.payload.data) {
      if (isApiKey(key)) delete app.payload.data[key];
    }
  }

  if (app?.static?.data) {
    for (const key in app.static.data) {
      if (isApiKey(key)) delete app.static.data[key];
    }
  }

  if (import.meta.client && app?.runWithContext) {
    app.runWithContext(() =>
      clearNuxtData((key) => isApiKey(String(key || '')))
    );
  }
};

export const getAllowedLocales = () => ALLOWED_LOCALES;
export { getLocaleByUrl };
